import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { Mail, CheckCircle, AlertCircle, Loader } from 'lucide-react';


const PortalEmailVerification = () => {
  const { team_id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [verifying, setVerifying] = useState(!!token);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState('');
  const [contactInfo, setContactInfo] = useState(null);

  useEffect(() => {
    if (token) {
      verifyEmail();
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await api.post(`/portal/${team_id}/verify-email`, {
        token: token
      });

      if (response.data.success) {
        setVerified(true);
        setContactInfo(response.data);
        
        // Redirect to password setup after 2 seconds
        setTimeout(() => {
          navigate(`/portal/${team_id}/set-password`, {
            state: {
              contact_id: response.data.contact_id,
              email: response.data.email,
              client_name: response.data.client_name
            }
          });
        }, 2000);
      }
    } catch (err) {
      console.error('Verification error:', err);
      const errorData = err.response?.data;
      
      if (errorData?.error?.includes('expired')) {
        setError('This verification link has expired. Please request a new one.');
      } else {
        setError(errorData?.error || 'Verification failed. Please try again or contact support.');
      }
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-block p-4 bg-white/10 rounded-2xl backdrop-blur-sm mb-4">
            <Mail className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Email Verification</h1>
        </div>

        {/* Verification Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Verifying State */}
          {verifying && (
            <div className="text-center py-8">
              <Loader className="w-16 h-16 text-purple-600 animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Verifying your email...
              </h2>
              <p className="text-gray-600">
                Please wait while we verify your email address
              </p>
            </div>
          )}

          {/* Success State */}
          {verified && !verifying && (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Email Verified Successfully!
              </h2>
              <p className="text-gray-600 mb-4">
                Welcome, {contactInfo?.email}
              </p>
              <p className="text-sm text-gray-500">
                Redirecting you to set up your password...
              </p>
            </div>
          )}

          {/* Error State */}
          {error && !verifying && (
            <div>
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                  <AlertCircle className="w-10 h-10 text-red-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Verification Failed
                </h2>
                <p className="text-gray-600 mb-6">
                  {error}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                {error.includes('expired') && (
                  <button
                    onClick={() => navigate(`/portal/${team_id}/resend-verification`)}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-200"
                  >
                    Request New Verification Link
                  </button>
                )}
                
                <button
                  onClick={() => navigate(`/portal/${team_id}`)}
                  className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-all duration-200"
                >
                  Back to Login
                </button>
              </div>
            </div>
          )}

          {/* No Token State */}
          {!token && !verifying && (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mb-4">
                <AlertCircle className="w-10 h-10 text-yellow-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Invalid Verification Link
              </h2>
              <p className="text-gray-600 mb-6">
                This link appears to be invalid. Please check your email for the correct verification link.
              </p>
              
              <button
                onClick={() => navigate(`/portal/${team_id}`)}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-200"
              >
                Back to Login
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-purple-200">
            Need help? Contact your administrator
          </p>
        </div>
      </div>
    </div>
  );
};

export default PortalEmailVerification;
